
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { extend } from "vee-validate";

import CsvUploader from "@/components/invite/CsvUploaderNew.vue";
import UiButton from "@/components/button/ui-button.vue";
import UserInvitationData from "@/components/invite/UserInvitationDataResources.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import ModalFullScreen from "@/components/modal/ModalFullScreenNew.vue";
import ModalTabs from "@/components/modal/ModalTabsNew.vue";
import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
import HoveredIcon from "@/components/icons/HoveredIcon.vue";
import { inviteContractors } from "@/services/inviteContractors";
import getAuthData from "@/utils/getAuthData";
import Message, { MessageDuration } from "@/data/notificationsMessages";
import LIST_MANAGERS from "@/apollo/managers/ListSingleManagers.gql";
import { INVITATION, NOTIFICATION } from "@/entities/Events";
import { Company } from "@/interface";
import { invitationModal } from "@/data/modalTabs";
import ModalContent from "@/components/modal/ModalContent.vue";
import AlertModel from "@/components/alert/AlertModel.vue";

import TOGGLE_IS_PUBLIC from "@/apollo/profile/ToggleIsPublic.gql";
import AUTO_APPROVE_PUBLIC_CONTRACTOR from "@/apollo/profile/AutoApprovePublicContractor.gql";

interface UserInterface {
  id: string | null;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  valid: boolean;
  processing: boolean;
  is_public: boolean;
}

@Component({
  components: {
    ModalContent,
    ModalFullScreen,
    CsvUploader,
    UiButton,
    UserInvitationData,
    PlusIcon,
    ModalTabs,
    HoveredIcon,
    ModalSectionsDivider,
    AlertModel,
  },
  apollo: {
    managers: {
      query: LIST_MANAGERS,
      variables() {
        return this.managerArgs;
      },
      update(data) {
        this.hasResourceAccess = data.manager[0]?.resources_access_allowed;
        return data.manager;
      },
    },
  },
})
export default class ContractorInvite extends Vue {
  @Prop({ type: Object, default: undefined }) readonly company!: Company;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isManager!: boolean;
  @Prop({ type: String, required: true })
  readonly companyBusinessName!: string;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;

  tabs: string[] = invitationModal;
  selectedTabIndex: number = 0;
  csvUploadCompleted: boolean = false;
  hasResourceAccess: boolean = false;
  csvParseOutput: UserInterface[] = [];
  customMessage: string = "";
  showInformationalModal: boolean = false;
  pendingInviteData: { drafted: boolean, users: UserInterface[] | null } | null = null;

  get allowDelete(): boolean {
    return this.users.length > 1;
  }

  get managerArgs() {
    const filterConditions = [
      {
        user_id: { _eq: this.currentUserId },
      },
    ];
    return {
      where: {
        _and: filterConditions,
      },
    };
  }

  beforeMount(): void {
    extend("unique", {
      params: [{ name: "usersToCheck" }],
      validate: (v: string, params: Record<string, any>) => {
        let count = 0;
        params.usersToCheck.forEach((u: UserInterface) => {
          if (u.email === v) {
            count++;
          }
        });
        return count <= 1;
      },
      message: "Must be unique",
    });
    extend("contractorExists", {
      params: [{ name: "usersToCheck" }],
      validate: async (
        v: string,
        params: Record<string, any>
      ): Promise<boolean> => {
        const currentContractor = params.usersToCheck.find(
          (u: UserInterface) => u.email == v
        );
        if (!currentContractor) {
          return false;
        }
        currentContractor.processing = true;
        try {
          const uri = `${process.env.VUE_APP_LAMBDA}/contractor-invite-email-validation`;
          const { headers } = await getAuthData();
          const { data }: any = await this.axios.post(
            uri,
            {
              data: {
                email: v.toLowerCase(),
                companyId: this.companyId,
              },
            },
            { headers }
          );
          const { user, valid, message } = data;

          if (user) {
            const { id, givenName, familyName } = user;
            currentContractor.id = id;
            currentContractor.givenName = givenName || "";
            currentContractor.familyName = familyName || "";
          } else if (currentContractor.id) {
            currentContractor.id = null;
            currentContractor.givenName = "";
            currentContractor.familyName = "";
          }
          this.customMessage = message;
          return valid;
        } catch (error) {
          // this.customMessage = error.message || error;
          return true;
        } finally {
          currentContractor.processing = false;
        }
      },
      message: () => {
        return this.customMessage;
      },
    });
  }

  handleCsvUploadUsers(users) {
    this.invite(false, users);
  }

  @Watch("selectedTabIndex")
  watchSelectedTabIndex(index: number): void {
    if (index === 0) {
      this.users = [
        {
          id: null,
          email: "",
          givenName: "",
          familyName: "",
          valid: false,
          processing: false,
          is_public: false,
        },
      ];
      return;
    }
    this.csvParseOutput = [];
  }
  loading: boolean = false;
  get uploading(): boolean {
    return this.loading;
  }
  get invitationTabUsers(): UserInterface[] {
    return this.selectedTabIndex == 0 ? this.users : this.csvParseOutput;
  }
  userTemplate: UserInterface = {
    id: null,
    email: null,
    givenName: null,
    familyName: null,
    valid: false,
    processing: false,
    is_public: false,
  };
  users: UserInterface[] = [
    {
      id: null,
      email: "",
      givenName: "",
      familyName: "",
      valid: false,
      processing: false,
      is_public: false,
    },
  ];
  updateTabIndex(index: number): void {
    this.selectedTabIndex = index;
  }
  handleDeleteRow(index: number): void {
    this.users.splice(index, 1);
    if (!this.users.length) {
      this.users = [
        {
          id: null,
          email: "",
          givenName: "",
          familyName: "",
          valid: false,
          processing: false,
          is_public: false,
        },
      ];
    }
  }
  handleInformationalModalClose(): void {
    this.showInformationalModal = false;
    if (this.pendingInviteData) {
      this.confirmAndSubmit(this.pendingInviteData.drafted, this.pendingInviteData.users);
      this.pendingInviteData = null;
    }
  }

  async invite(
    drafted: boolean = false,
    users: UserInterface[] | null = null
  ): Promise<void> {
    // Store the invite data to use after modal is closed
    this.pendingInviteData = { drafted, users };
    // Show the informational modal first
    this.showInformationalModal = true;
  }

  async confirmAndSubmit(
    drafted: boolean = false,
    users: UserInterface[] | null = null
  ): Promise<void> {
    try {
      this.loading = true;
      const usersArray = users || this.users;
      const userObjects = usersArray.map((u) => ({
        user_id: u.id,
        givenName: u.givenName || "",
        familyName: u.familyName || "",
        email: (u.email || "").toLowerCase(),
        is_public: u.is_public || false,
      }));
      const invitedContractorsIds = await inviteContractors({
        users: userObjects,
        companyId: this.companyId,
        companyBusinessName: this.companyBusinessName,
        invitedByUserId: this.currentUserId,
        drafted: drafted ? true : false,
      });
      invitedContractorsIds.forEach(
        async (contractorId: any, index: number) => {
          if (userObjects[index].is_public) {
            await this.autoApprovePublicContractor(contractorId.id);
          }
        }
      );
      if (users) {
        this.csvUploadCompleted = true;
      }
      const successMessage =
        this.isManager && !this.hasResourceAccess
          ? "Your request for access has been submitted to your Admin. Once they have approved the request, this person will appear in your private database."
          : "Invited";

      const duration = this.isManager
        ? MessageDuration.LONG
        : MessageDuration.DEFAULT;
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(
          drafted
            ? "Profile(s) created without sending an invite successfully!"
            : successMessage,
          duration
        )
      );
      this.clearUsers();
      this.clearCsv();
      this.$eventBus.emit(INVITATION);
      window.location.reload();
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    } finally {
      this.loading = false;
    }
  }
  clearUsers(): void {
    this.users = [
      {
        id: null,
        email: "",
        givenName: "",
        familyName: "",
        valid: false,
        processing: false,
        is_public: false,
      },
    ];
  }
  clearCsv(): void {
    this.csvParseOutput = [];
  }
  add(): void {
    this.users.push({ ...this.userTemplate });
  }
  handleCancel(): void {
    this.$emit("input", false);
    this.selectedTabIndex = 0;
    this.clearUsers();
    this.clearCsv();
  }
  extensionAlert(): void {
    this.$eventBus.emit(
      NOTIFICATION,
      Message.errorMessage("Make sure your file has .csv extension")
    );
  }
  invalidHeaderRow(): void {
    this.$eventBus.emit(
      NOTIFICATION,
      Message.errorMessage(
        "Make sure your csv file has a header row with values 'email', 'given_name', 'family_name'",
        MessageDuration.MEDIUM
      )
    );
  }
  async autoApprovePublicContractor(contractorId: string): Promise<void> {
    let result = await this.$apollo.mutate({
      mutation: AUTO_APPROVE_PUBLIC_CONTRACTOR,
      variables: {
        contractorId: contractorId,
      },
    });
    const userId = result.data.update_contractor.returning[0].user.id;
    await this.$apollo.mutate({
      mutation: TOGGLE_IS_PUBLIC,
      variables: {
        userId: userId,
        _set: { is_public: true },
      },
    });
  }
}
